import React from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import FrequentlyAskedQuestions, {
  FaqParagraph,
} from '@rover/components/src/FrequentlyAskedQuestions';
import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { Link, Paragraph } from '@rover/kibble/core';
import getSearchUrl from '@rover/react-lib/src/utils/getSearchUrl';
import type { CountryConfigurationUrlsLocalizedLinkUrls } from '@rover/rsdk/src/apiClient/latest/model/countryConfigurationUrlsLocalizedLinkUrls';
import type { SearchFilters } from '@rover/types/src/SearchFilters';

import FirstFAQIsOpen from './FirstFAQIsOpen';
import FrequentlyAskedQuestion from './FrequentlyAskedQuestion';
import UnorderedList, { ListItem } from './UnorderedList';

type DogWalkingServiceFaqsProps = {
  htmlRef?: React.RefObject<HTMLElement>;
  localizedLinkUrls: CountryConfigurationUrlsLocalizedLinkUrls;
  locale: Locale;
  searchFilters: Partial<SearchFilters>;
};

const DogWalkingServiceFaqs: React.FC<DogWalkingServiceFaqsProps> = ({
  htmlRef,
  localizedLinkUrls: links,
  locale,
  searchFilters,
}) => {
  const { t } = useLingui();
  const searchUrl = getSearchUrl(searchFilters, locale);
  return (
    <FrequentlyAskedQuestions
      htmlRef={htmlRef}
      data-testid="dog-walking-service-faqs"
      // override defaults in FrequentlyAskedQuestions
      mt="0x"
      pb="0x"
    >
      <FrequentlyAskedQuestions.Header webProps={{ responsive: false }}>
        <Trans>Dog walking near you</Trans>
      </FrequentlyAskedQuestions.Header>
      <FrequentlyAskedQuestions.Subheader>
        <Trans>
          Give your dog a 30 or 60 minute walk with a trusted dog walker around your neighborhood.
        </Trans>
      </FrequentlyAskedQuestions.Subheader>
      <FirstFAQIsOpen>
        {/* 1 */}
        <FrequentlyAskedQuestion question={t`What are the best dog walking options?`}>
          <>
            <FaqParagraph>
              <Trans>
                The best dog walk is the one your dog looks forward to. Whether that's a casual
                stroll around the neighborhood or an urban adventure full of sniffing and
                exploration, Rover offers flexible dog walking options to fit your dog's energy
                level and your schedule:
              </Trans>
            </FaqParagraph>
            <UnorderedList>
              <ListItem>
                <Trans>30-minute walks — great for quick potty breaks and mid-day exercise</Trans>
              </ListItem>
              <ListItem>
                <Trans>60-minute walks — ideal for high-energy dogs or longer adventures</Trans>
              </ListItem>
            </UnorderedList>
            <Paragraph webProps={{ maxWidth: 'none' }} mt="2x">
              <Trans>
                Read through dog walker profiles to match their experience with your dog's
                personality and needs. You can also{' '}
                <Link href={links.addOrDeletePetProfile}>fill out your pet's profile</Link> with
                details to help your walker get to know your dog more.
              </Trans>
            </Paragraph>
            <FaqParagraph>
              <Trans>
                For a peace of mind, we recommend scheduling a Meet & Greet before booking. Meet &
                Greets are a great opportunity to go over walking equipment, preferred routes, ideal
                pace, and training cues to help keep your dog safe and happy.
              </Trans>
            </FaqParagraph>
          </>
        </FrequentlyAskedQuestion>

        {/* 2 */}
        <FrequentlyAskedQuestion
          question={t`What are reliable dog walking options during work hours?`}
        >
          <>
            <FaqParagraph>
              <Trans>
                With the largest network of pet sitters and walkers, Rover makes finding dog walking
                during work hours simple.
              </Trans>
            </FaqParagraph>
            <FaqParagraph>
              <Trans>
                When searching, enter your preferred dates and time slots to see available walkers.
                You can book walks anytime during the day. Filter for your local dog walker's
                availability by using the time slots below:
              </Trans>
            </FaqParagraph>
            <UnorderedList>
              <ListItem>
                <Trans>6am-11am</Trans>
              </ListItem>
              <ListItem>
                <Trans>11am-3pm</Trans>
              </ListItem>
              <ListItem>
                <Trans>3pm-10pm</Trans>
              </ListItem>
            </UnorderedList>
            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x" mt="2x">
              <Trans>
                Need more flexibility? You can schedule multiple walks a day and set them to repeat
                weekly so that your dog gets consistent exercise and care from a walker they know
                and love.
              </Trans>
            </Paragraph>
          </>
        </FrequentlyAskedQuestion>

        {/* 3 */}
        <FrequentlyAskedQuestion question={t`Do dog walkers on Rover handle large breeds?`}>
          <FaqParagraph>
            <Trans>
              Yes! There will be dog walkers on Rover who have experience walking dogs of all
              breeds, sizes, and personalities. When searching for walkers, use the dog size filter
              and select 41-100 or 101+ pounds. Your results will show dog walkers who accept large
              dog breeds. From there, read through walker profiles and pet parent reviews. Reviews
              that mention dog size and temperament to help you find the right fit. Before booking,
              fill out your pet's profile and set up a Meet & Greet. Both of these steps can help
              set your dog and the dog walker up for success.
            </Trans>
          </FaqParagraph>
        </FrequentlyAskedQuestion>

        {/* 4 */}
        {'whatIsARecurringService' in links && 'whatAreRoverCardsAndHowDoISendThem' in links && (
          <FrequentlyAskedQuestion question={t`Who provides recurring dog walking subscriptions?`}>
            <FaqParagraph>
              <Trans>
                When searching for dog walkers on Rover, select &quot;Repeat Weekly&quot; and the
                days you'd like your walks to happen. This will show dog walkers who offer{' '}
                <Link href={links.whatIsARecurringService} target="_blank">
                  recurring walks
                </Link>{' '}
                that fit your schedule. While Rover doesn't offer traditional subscription services,
                recurring walks come with their own perks such as: flexibility to modify upcoming
                services (up to five weeks in advance), same-day cancellation, and automatic
                billing. Dog walkers who provide recurring services must send a report card, also
                called a{' '}
                <Link href={links.whatAreRoverCardsAndHowDoISendThem} target="_blank">
                  Rover Card
                </Link>
                , before they receive payment for walking.
              </Trans>
            </FaqParagraph>
          </FrequentlyAskedQuestion>
        )}

        {/* 5 */}
        {'howDoesDogWalkingMapFeatureWork' in links && (
          <FrequentlyAskedQuestion question={t`What dog walking services offer GPS tracking?`}>
            <FaqParagraph>
              <Trans>
                Rover doesn't offer live GPS tracking for dog walks, but you can request your dog
                walker to send a report card with a map of your dog's walk. These report cards, also
                called Rover Cards, are automatically required for recurring walk services. Each
                report card includes{' '}
                <Link href={links.howDoesDogWalkingMapFeatureWork} target="_blank">
                  a map of the walking route
                </Link>
                , total walk time, and distance traveled. You can also request fun photo and text
                updates with the report card so you can see how your dog's walk went.
              </Trans>
            </FaqParagraph>
          </FrequentlyAskedQuestion>
        )}

        {/* 6 */}
        {'howDoITipMySitter' in links && (
          <FrequentlyAskedQuestion question={t`Should I tip my dog walker through Rover?`}>
            <FaqParagraph>
              <Trans>
                Once the booking is complete, you'll have the option to tip your dog walker directly
                through Rover. Walkers{' '}
                <Link href={links.howDoITipMySitter} target="_blank">
                  keep 100% of the tips
                </Link>{' '}
                you leave them. If you are wondering about how much to tip, read this tipping guide.
              </Trans>
            </FaqParagraph>
          </FrequentlyAskedQuestion>
        )}

        {/* 7 */}
        <FrequentlyAskedQuestion question={t`How do I book dog walking?`}>
          <FaqParagraph>
            <Trans>
              When you have <Link href={searchUrl}>found a dog walker</Link>, contact them through
              their profile and schedule a Meet &amp; Greet. After your Meet &amp; Greet, you and
              your walker can confirm the service by clicking &quot;Book It Now&quot; in the Rover
              app. You can also book walks without meeting first, but we highly recommend a Meet
              &amp; Greet to help your dog and walker get to know each other.
            </Trans>
          </FaqParagraph>
        </FrequentlyAskedQuestion>
      </FirstFAQIsOpen>
    </FrequentlyAskedQuestions>
  );
};

export default DogWalkingServiceFaqs;
